/**
 * RFC-0088: Yjs document connection and lifecycle hook.
 *
 * Connects to the backend WebSocket endpoint and maintains a Yjs document
 * that syncs bidirectionally with the server's pycrdt document.
 *
 * CRITICAL: A fresh Y.Doc must be created for each projectId/canvasId
 * combination. Reusing a Y.Doc across projects causes cross-project data
 * contamination because the Yjs CRDT sync protocol sends ALL local state
 * to the server on connect — including stale data from a previous project.
 */

import { useEffect, useState } from 'react'
import * as Y from 'yjs'
import { WebsocketProvider } from 'y-websocket'
import { getConfiguredApiBase, getAuthToken } from '../api/client'
import { createDebug, setDebugClientID } from '../utils/debugLog'

const log = createDebug('dw:ydoc:lifecycle')

interface UseYDocOptions {
  projectId: string
  canvasId?: string
  enabled?: boolean
}

interface UseYDocResult {
  /** The Yjs document (fresh per projectId/canvasId) */
  ydoc: Y.Doc
  /** Whether the initial sync with the server is complete */
  synced: boolean
  /** Whether the WebSocket connection is live */
  connected: boolean
}

/**
 * Convert an HTTP(S) API base URL to a WebSocket URL.
 * e.g. "http://localhost:9000" → "ws://localhost:9000"
 *      "https://api.example.com" → "wss://api.example.com"
 */
function toWsUrl(httpUrl: string): string {
  return httpUrl.replace(/^http/, 'ws')
}

export function useYDoc({
  projectId,
  canvasId = 'agent',
  enabled = true,
}: UseYDocOptions): UseYDocResult {
  // State for the Y.Doc instance, keyed by projectId/canvasId.
  // A fresh Y.Doc is created inside the useEffect below whenever the key
  // changes, and the state is updated to trigger a re-render with the new doc.
  const [ydoc, setYdoc] = useState<Y.Doc>(() => new Y.Doc())
  const [synced, setSynced] = useState(false)
  const [connected, setConnected] = useState(false)

  // Expose YDoc on window for E2E testing (dev mode only)
  useEffect(() => {
    if (import.meta.env.DEV) {
      (window as unknown as Record<string, unknown>).__ydoc = ydoc
    }
    return () => {
      if (import.meta.env.DEV) {
        delete (window as unknown as Record<string, unknown>).__ydoc
      }
    }
  }, [ydoc])

  // Single comprehensive effect: manages both Y.Doc lifecycle and WebSocket.
  // When projectId or canvasId changes, this effect creates a FRESH Y.Doc,
  // connects it to the new room, and destroys the old one on cleanup.
  // This eliminates the race condition where a stale Y.Doc connects to a
  // new project's room and contaminates it with old data.
  useEffect(() => {
    if (!enabled || !projectId) return

    // Create a fresh Y.Doc for this project/canvas combination.
    // This doc has NO data from any previous project.
    const freshDoc = new Y.Doc()
    log('created fresh Y.Doc for %s/%s (clientID=%d)', projectId, canvasId, freshDoc.clientID)

    // Update state so consumers (useYCanvas, etc.) get the new doc
    setYdoc(freshDoc)
    setSynced(false)
    setConnected(false)

    // Build WebSocket URL
    const apiBase = getConfiguredApiBase()
    const wsBase = toWsUrl(apiBase)
    const roomName = `${projectId}/${canvasId}`
    const wsUrl = `${wsBase}/ws/yjs`

    let cancelled = false
    let provider: WebsocketProvider | null = null

    const connect = async () => {
      const token = await getAuthToken()
      if (cancelled) return

      const params: Record<string, string> = {}
      if (token) {
        params['token'] = token
      }

      provider = new WebsocketProvider(wsUrl, roomName, freshDoc, {
        connect: true,
        params,
      })

      provider.on('status', async ({ status }: { status: string }) => {
        if (cancelled) return
        log('ws status=%s room=%s', status, roomName)
        console.log('[YDoc] WS status:', status)
        setConnected(status === 'connected')
        if (status === 'disconnected') {
          try {
            const freshToken = await getAuthToken()
            if (freshToken && provider?.params) {
              provider.params['token'] = freshToken
            }
            if (!freshToken) {
              log('auth token null — potential session expiry')
              console.warn('[YDoc] Auth token is null — session may have expired')
            }
          } catch (err) {
            log('token refresh failed: %O', err)
            console.warn('[useYDoc] Failed to refresh token on disconnect:', err)
          }
        }
      })

      provider.on('sync', (isSynced: boolean) => {
        if (cancelled) return
        log('ws synced=%s room=%s', isSynced, roomName)
        console.log('[YDoc] synced:', isSynced)
        setSynced(isSynced)
      })

      setDebugClientID(freshDoc.clientID)

      // Expose Y.Doc for Playwright tests (dev only)
      if (import.meta.env.DEV) {
        const testWindow = window as Window & { __ydoc?: Y.Doc }
        testWindow.__ydoc = freshDoc
      }
    }

    connect()

    return () => {
      cancelled = true
      log('cleanup room=%s (clientID=%d)', roomName, freshDoc.clientID)
      if (provider) {
        provider.destroy()
      }
      freshDoc.destroy()
      setConnected(false)
      setSynced(false)
    }
  }, [projectId, canvasId, enabled])

  return { ydoc, synced, connected }
}
